// Desplegable con buscador (PE-C1). Reemplaza a <select> en listas largas: con más de 10 opciones se escribe para
// filtrar (sin distinguir tildes ni mayúsculas); con 10 o menos queda el <select> nativo de siempre (misma API).
// Patrón "combobox" de WAI-ARIA, como BuscadorOpciones (bloque 5d): flechas para moverse, Enter para elegir, Esc para
// cerrar sin cambiar; con el dedo o el mouse se toca la opción. Solo acepta una de las opciones (no texto libre).
// Si el campo no es obligatorio, la primera opción es la vacía ("vacio"), que deja el valor en ''.
// No se usa en el menú lateral.

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { normalizarBusqueda } from '../lib/sso-documentos';
import './SelectBuscable.css';

export interface OpcionSelect { value: string; label: string; detalle?: string }

export const UMBRAL_BUSCADOR = 10;

export interface PropsSelectBuscable {
  opciones: OpcionSelect[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;             // sin él, el nombre lo da la <label> que envuelve el campo
  id?: string;
  obligatorio?: boolean;          // sin opción vacía
  vacio?: string;                 // texto de la opción vacía (o del marcador si es obligatorio y está vacío)
  placeholder?: string;
  disabled?: boolean;
  inputStyle?: React.CSSProperties;
  className?: string;
  tema?: 'oscuro' | 'claro';
  umbral?: number;
}

export function filtrarSelect(opciones: OpcionSelect[], consulta: string): OpcionSelect[] {
  const q = normalizarBusqueda(consulta);
  if (!q) return opciones;
  return opciones.filter(o => normalizarBusqueda(`${o.label} ${o.detalle ?? ''}`).includes(q));
}

export default function SelectBuscable(p: PropsSelectBuscable) {
  const { opciones, value, onChange, obligatorio = false, vacio = 'Sin elegir', disabled, tema = 'oscuro', umbral = UMBRAL_BUSCADOR } = p;
  const auto = useId();
  const id = p.id ?? `${auto}-campo`;
  const listaId = `${auto}-lista`;
  const [abierto, setAbierto] = useState(false);
  const [consulta, setConsulta] = useState('');
  const [activo, setActivo] = useState(0);
  const listaRef = useRef<HTMLUListElement>(null);
  const cierre = useRef<ReturnType<typeof setTimeout> | null>(null);

  const elegida = opciones.find(o => o.value === value) ?? null;
  // La opción vacía va primero, y solo sin consulta (escribir es para buscar una de verdad).
  const visibles = useMemo(() => {
    const f = filtrarSelect(opciones, consulta);
    return !obligatorio && !consulta.trim() ? [{ value: '', label: vacio }, ...f] : f;
  }, [opciones, consulta, obligatorio, vacio]);

  useEffect(() => () => { if (cierre.current) clearTimeout(cierre.current); }, []);
  useEffect(() => {
    if (!abierto) return;
    (listaRef.current?.children[activo] as HTMLElement | undefined)?.scrollIntoView?.({ block: 'nearest' });
  }, [activo, abierto]);

  const clase = `sb${tema === 'claro' ? ' sb-claro' : ''}${p.className ? ` ${p.className}` : ''}`;

  // Lista corta: el <select> nativo (mejor en el celular y sin nada que aprender).
  if (opciones.length <= umbral) {
    return (
      <div className={clase}>
        <select id={id} aria-label={p.ariaLabel} className="sb-campo" style={p.inputStyle} value={value} disabled={disabled}
          onChange={e => onChange(e.target.value)}>
          {(!obligatorio || value === '') && <option value="" disabled={obligatorio}>{vacio}</option>}
          {opciones.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );
  }

  function abrir() {
    if (cierre.current) { clearTimeout(cierre.current); cierre.current = null; }
    if (abierto || disabled) return;
    setConsulta('');
    const i = (!obligatorio ? [{ value: '' }, ...opciones] : opciones).findIndex(o => o.value === value);
    setActivo(i >= 0 ? i : 0);
    setAbierto(true);
  }
  function cerrar() { setAbierto(false); setConsulta(''); }
  function elegir(o: OpcionSelect) {
    if (cierre.current) { clearTimeout(cierre.current); cierre.current = null; }
    if (o.value !== value) onChange(o.value);
    cerrar();
  }
  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!abierto) { abrir(); return; }
      if (!visibles.length) return;
      const paso = e.key === 'ArrowDown' ? 1 : -1;
      setActivo(a => (a + paso + visibles.length) % visibles.length);
    } else if (e.key === 'Enter') {
      if (!abierto) return;
      e.preventDefault();
      const o = visibles[activo];
      if (o) elegir(o);
    } else if (e.key === 'Escape') {
      if (abierto) { e.preventDefault(); e.stopPropagation(); cerrar(); }
    } else if (e.key === 'Tab') cerrar();
  }

  const activoId = abierto && visibles[activo] ? `${auto}-op-${activo}` : undefined;
  const texto = elegida?.label ?? '';
  return (
    <div className={clase}>
      <input id={id} type="text" role="combobox" className="sb-campo" style={p.inputStyle} aria-label={p.ariaLabel} aria-expanded={abierto}
        aria-controls={listaId} aria-autocomplete="list" aria-activedescendant={activoId} autoComplete="off" spellCheck={false}
        disabled={disabled} placeholder={abierto ? (texto || p.placeholder || 'Escribe para buscar…') : (p.placeholder ?? (obligatorio ? 'Elige…' : vacio))}
        value={abierto ? consulta : texto} title={texto || undefined}
        onFocus={abrir} onClick={abrir}
        onChange={e => { if (!abierto) abrir(); setConsulta(e.target.value); setActivo(0); }}
        onKeyDown={onKeyDown}
        // Con el dedo, el campo puede perder el foco antes del toque en la opción: se cierra un instante después.
        onBlur={() => { cierre.current = setTimeout(cerrar, 150); }} />
      <span className="sb-flecha" aria-hidden="true">▼</span>
      {abierto && (
        <ul ref={listaRef} id={listaId} role="listbox" aria-label={p.ariaLabel} className="sb-lista">
          {visibles.length === 0 ? <li className="sb-sin" role="presentation">Sin resultados</li> : visibles.map((o, i) => (
            <li key={o.value || '__vacio'} id={`${auto}-op-${i}`} role="option" aria-selected={o.value === value}
              className={`sb-op${i === activo ? ' activa' : ''}${o.value === '' ? ' vacia' : ''}`}
              onMouseDown={e => e.preventDefault()} onMouseEnter={() => setActivo(i)} onClick={() => elegir(o)}>
              {o.label}{o.detalle && <small>{o.detalle}</small>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
